import * as React from 'react';

import { useRequest } from 'ahooks';
import { Col, Form, Input, Row, Select } from 'antd';

import { CheckInputbox, PrimitiveTreeSelect } from 'components';
import { useAttributeAction, useAttributeValue } from 'context';
import { getCategories } from 'services';
import type { TreeSelectSource } from 'types';
import {
  generateTreeSelectData,
  inputRule,
  localizeAttributeType,
  normalizeToString,
} from 'utils';

type FieldType = Pick<
  ReturnType<typeof useAttributeValue>,
  'name' | 'type' | 'helpText' | 'isFilter' | 'categories'
>;

const attributeConfigTypes = [
  'Text',
  'Number',
  'Select',
  'CheckBox',
] satisfies NonNullable<FieldType['type']>[];

type AttributeFormInformationProps = {
  handleSelectedCategories: (
    selectedCategories: NonNullable<FieldType['categories']>,
  ) => void;
};

const AttributeFormInformation = ({
  handleSelectedCategories,
}: AttributeFormInformationProps) => {
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { name, type, helpText, isFilter } = useAttributeValue();
  const action = useAttributeAction();

  const { categories = [] } = categoriesData ?? {};
  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );

  return (
    <Row className='rounded bg-white px-6 py-8'>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نام'
          name='name'
          rules={[inputRule.required, inputRule.string]}
          initialValue={name}
        >
          <Input placeholder='نام فیلد را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='نوع'
          name='type'
          rules={[inputRule.required]}
          initialValue={type}
        >
          <Select<typeof type>
            placeholder='نوع فیلد را انتخاب کنید'
            options={attributeConfigTypes.map(type => {
              const { label } = localizeAttributeType(type);
              return {
                value: type,
                label,
              };
            })}
            onChange={value => {
              action({
                type: 'UPDATE_ATTRIBUTE_TYPE',
                payload: {
                  type: value,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='توضیحات مختصر'
          name='helpText'
          rules={[inputRule.string, inputRule.abstraction]}
          initialValue={helpText}
        >
          <Input.TextArea
            autoSize
            placeholder='توضیحات مختصر فیلد را وارد کنید'
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='قابلیت فیلتر'
          name='isFilter'
          valuePropName='checked'
          initialValue={isFilter}
        >
          <CheckInputbox>قابلیت فیلتر توسط کاربر</CheckInputbox>
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='دسته‌بندی‌ها' name='categories'>
          <PrimitiveTreeSelect<TreeSelectSource[]>
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های فیلد را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
            onChange={values => {
              handleSelectedCategories(
                values.map(({ label, value }) => ({
                  id: normalizeToString(value),
                  slug: normalizeToString(label),
                  subSlug: normalizeToString(label),
                  title: normalizeToString(label),
                  status: 'Active',
                  values: [],
                })),
              );
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { AttributeFormInformation };
